/**
 * FundRaisingApplicationPanel
 * Sectioned editor for a single grant application.
 * Sections: Problem Statement | Innovation | Technical Plan | Budget | Team | Impact
 * Shows version log and allows status transitions.
 */
import React, { useState, useEffect } from 'react';

const API = '/api/opo/fundraising';

const SECTIONS = [
  { key: 'problem_statement', label: 'Problem Statement' },
  { key: 'innovation',        label: 'Innovation' },
  { key: 'technical_plan',    label: 'Technical Plan' },
  { key: 'budget',            label: 'Budget' },
  { key: 'team',              label: 'Team' },
  { key: 'impact',            label: 'Impact' },
];

const STATUS_TRANSITIONS = {
  drafting:  ['review'],
  review:    ['ready', 'drafting'],
  ready:     ['submitted', 'review'],
  submitted: ['pending'],
  pending:   ['approved', 'rejected'],
  approved:  [],
  rejected:  ['drafting'],
};

const STATUS_COLORS = {
  drafting: '#6366f1', review: '#f59e0b', ready: '#10b981', submitted: '#3b82f6',
  pending: '#8b5cf6', approved: '#10b981', rejected: '#ef4444',
};

function fmt(n) { return n != null ? `$${Number(n).toLocaleString('en-CA')}` : '—'; }
function fmtDate(s) { return s ? new Date(s).toLocaleDateString('en-CA') : '—'; }

export default function FundRaisingApplicationPanel({ applicationId, onClose }) {
  const [data, setData] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [activeView, setActiveView] = useState('sections');

  const load = () => {
    fetch(`${API}/applications/${applicationId}`)
      .then(r => r.json())
      .then(setData)
      .catch(() => {});
  };

  useEffect(() => { load(); }, [applicationId]);

  if (!data) return <div className="fr-loading">Loading application…</div>;

  const { application, sections, agent_history } = data;

  const handleEditStart = (key) => {
    setEditingSection(key);
    setEditContent(sections[key] || '');
  };

  const handleSave = async () => {
    if (!editingSection) return;
    setSaving(true);
    try {
      await fetch(`${API}/applications/${applicationId}/sections/${editingSection}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editContent }),
      });
      setEditingSection(null);
      load();
    } catch (e) {
      alert('Save failed: ' + e.message);
    }
    setSaving(false);
  };

  const handleTransition = async (newStatus) => {
    setTransitioning(true);
    const r = await fetch(`${API}/applications/${applicationId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    if (r.ok) load();
    setTransitioning(false);
  };

  const handleRunBuilder = async () => {
    await fetch(`${API}/agents/builder/run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ application_id: applicationId }),
    });
    alert('Application Builder triggered. Sections will be drafted asynchronously.');
  };

  const handleRunCompliance = async () => {
    await fetch(`${API}/agents/compliance/run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ application_id: applicationId }),
    });
    alert('Compliance check triggered.');
  };

  const nextStatuses = STATUS_TRANSITIONS[application.status] || [];

  return (
    <div className="fr-channel-detail">
      <div className="fr-channel-detail-header">
        <h2 className="fr-channel-detail-name">{application.name}</h2>
        <div className="fr-channel-detail-meta">
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[application.status] || '#6b7280', display: 'inline-block', marginRight: 6 }} />
          <span className="fr-muted">{application.status}</span>
          <span className="fr-badge fr-badge-region">{application.channel_name}</span>
          {application.submitted_at && <span className="fr-muted">Submitted: {fmtDate(application.submitted_at)}</span>}
          {application.award_amount_cad && <span className="fr-muted">Award: {fmt(application.award_amount_cad)}</span>}
          {application.compliance_score != null && (
            <span className={`fr-fit-score fr-fit-${application.compliance_score >= 90 ? 'high' : application.compliance_score >= 60 ? 'mid' : 'low'}`}>
              Compliance: {Math.round(application.compliance_score)}%
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {nextStatuses.map(s => (
            <button key={s} type="button" className="fr-btn-outline" onClick={() => handleTransition(s)} disabled={transitioning}>
              Move to: {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <button type="button" className="fr-btn-outline" onClick={handleRunBuilder}>AI Draft Sections</button>
          <button type="button" className="fr-btn-outline" onClick={handleRunCompliance}>Run Compliance</button>
        </div>
      </div>

      {/* View toggles */}
      <div className="fr-channel-tabs">
        {['sections', 'history', 'notes'].map(v => (
          <button key={v} type="button" className={`fr-channel-tab ${activeView === v ? 'active' : ''}`} onClick={() => setActiveView(v)}>
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </button>
        ))}
      </div>

      {/* Sections */}
      {activeView === 'sections' && (
        <div className="fr-app-sections">
          {SECTIONS.map(({ key, label }) => (
            <div key={key} className="fr-section-card">
              <div className="fr-section-title">{label}</div>
              {editingSection === key
                ? <>
                  <textarea
                    className="fr-section-textarea"
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    rows={10}
                  />
                  <div className="fr-section-actions">
                    <button type="button" className="fr-btn" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                    <button type="button" className="fr-btn-outline" onClick={() => setEditingSection(null)}>Cancel</button>
                  </div>
                </>
                : <>
                  <div className="fr-section-content">
                    {sections[key] || <span className="fr-muted">No content yet — click Edit or trigger AI Draft.</span>}
                  </div>
                  <div className="fr-section-actions" style={{ marginTop: 8 }}>
                    <button type="button" className="fr-btn-outline" onClick={() => handleEditStart(key)}>Edit</button>
                  </div>
                </>
              }
            </div>
          ))}
        </div>
      )}

      {/* History */}
      {activeView === 'history' && (
        <div className="fr-panel">
          <div className="fr-panel-title">Agent History</div>
          {(agent_history || []).length === 0
            ? <p className="fr-muted">No agent runs for this application.</p>
            : (agent_history || []).map(r => (
              <div key={r.id} className="fr-alert-row">
                <span className="fr-badge fr-badge-region" style={{ textTransform: 'capitalize' }}>{r.agent_type}</span>
                <span className={`fr-badge fr-badge-${r.status === 'completed' ? 'green' : r.status === 'failed' ? 'red' : 'info'}`}>{r.status}</span>
                <span className="fr-muted">{fmtDate(r.started_at)}</span>
                {r.confidence != null && <span className="fr-muted">Confidence: {Math.round(r.confidence)}%</span>}
                {r.summary_md && <span style={{ fontSize: 12, color: 'var(--text, #e8eaf6)', flex: 1 }}>{r.summary_md.slice(0, 120)}</span>}
              </div>
            ))
          }
        </div>
      )}

      {/* Notes */}
      {activeView === 'notes' && (
        <div className="fr-panel">
          <div className="fr-panel-title">Application Notes</div>
          {application.notes_md
            ? <pre className="fr-overview-md" style={{ fontSize: 13 }}>{application.notes_md}</pre>
            : <p className="fr-muted">No notes. Add notes via PATCH /applications/{applicationId}.</p>
          }
          {application.compliance_report_md && (
            <>
              <div className="fr-panel-title" style={{ marginTop: 16 }}>Compliance Report</div>
              <pre className="fr-overview-md" style={{ fontSize: 12 }}>{application.compliance_report_md}</pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}
